/**
 * @fileoverview Services Store
 * @description Zustand store for services state management
 * @module @xtream/frontend/store/services
 *
 * Features:
 * - Services list management
 * - Real-time status updates
 * - Progress tracking
 * - Filtering and search
 */

import { create } from 'zustand';
import type {
  IServiceWithRelations,
  IProgressData,
  ServiceStatus,
  IServiceStats,
} from '@xtream/shared-types';

// ═══════════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Service with progress data for display
 */
export interface ServiceWithProgress extends IServiceWithRelations {
  progress: IProgressData | undefined;
}

/**
 * Service filters
 */
export interface ServiceFilters {
  status?: ServiceStatus | 'ALL';
  search?: string;
}

/**
 * Services store state
 */
interface ServicesState {
  /** List of services */
  services: ServiceWithProgress[];
  /** Selected service for detail view */
  selectedService: ServiceWithProgress | null;
  /** Active filters */
  filters: ServiceFilters;
  /** Whether services are loading */
  isLoading: boolean;
  /** Whether a single service is loading */
  isLoadingService: boolean;
  /** Error message */
  error: string | null;
  /** Last fetch timestamp */
  lastFetched: number | null;
}

/**
 * Services store actions
 */
interface ServicesActions {
  /** Set all services */
  setServices: (services: IServiceWithRelations[]) => void;
  /** Add a new service */
  addService: (service: IServiceWithRelations) => void;
  /** Update an existing service */
  updateService: (id: string, data: Partial<IServiceWithRelations>) => void;
  /** Remove a service */
  removeService: (id: string) => void;
  /** Set selected service */
  selectService: (service: ServiceWithProgress | null) => void;
  /** Update service status (from WebSocket) */
  updateServiceStatus: (serviceId: string, status: ServiceStatus) => void;
  /** Update service progress (from WebSocket) */
  updateServiceProgress: (serviceId: string, progress: IProgressData) => void;
  /** Clear service progress */
  clearServiceProgress: (serviceId: string) => void;
  /** Set filters */
  setFilters: (filters: Partial<ServiceFilters>) => void;
  /** Clear filters */
  clearFilters: () => void;
  /** Set loading state */
  setLoading: (isLoading: boolean) => void;
  /** Set loading single service state */
  setLoadingService: (isLoading: boolean) => void;
  /** Set error */
  setError: (error: string | null) => void;
  /** Clear error */
  clearError: () => void;
  /** Get filtered services */
  getFilteredServices: () => ServiceWithProgress[];
  /** Get service by ID */
  getServiceById: (id: string) => ServiceWithProgress | undefined;
  /** Get service stats */
  getStats: () => IServiceStats;
  /** Reset store */
  reset: () => void;
}

// ═══════════════════════════════════════════════════════════════════════════
// INITIAL STATE
// ═══════════════════════════════════════════════════════════════════════════

const initialFilters: ServiceFilters = {
  status: 'ALL',
  search: '',
};

const initialState: ServicesState = {
  services: [],
  selectedService: null,
  filters: initialFilters,
  isLoading: false,
  isLoadingService: false,
  error: null,
  lastFetched: null,
};

// ═══════════════════════════════════════════════════════════════════════════
// STORE
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Zustand services store
 */
export const useServicesStore = create<ServicesState & ServicesActions>()((set, get) => ({
  ...initialState,

  setServices: (services) => {
    // Preserve progress data for existing services
    const currentServices = get().services;
    const servicesWithProgress: ServiceWithProgress[] = services.map((service) => {
      const existing = currentServices.find((s) => s.id === service.id);
      return {
        ...service,
        progress: existing?.progress as IProgressData | undefined,
      } as ServiceWithProgress;
    });

    set({
      services: servicesWithProgress,
      isLoading: false,
      error: null,
      lastFetched: Date.now(),
    });
  },

  addService: (service) => {
    set((state) => ({
      services: [...state.services, { ...service, progress: undefined }],
    }));
  },

  updateService: (id, data) => {
    set((state) => ({
      services: state.services.map((s) => (s.id === id ? { ...s, ...data } : s)),
      selectedService:
        state.selectedService?.id === id
          ? { ...state.selectedService, ...data }
          : state.selectedService,
    }));
  },

  removeService: (id) => {
    set((state) => ({
      services: state.services.filter((s) => s.id !== id),
      selectedService: state.selectedService?.id === id ? null : state.selectedService,
    }));
  },

  selectService: (service) => {
    set({ selectedService: service });
  },

  updateServiceStatus: (serviceId, status) => {
    set((state) => ({
      services: state.services.map((s) => (s.id === serviceId ? { ...s, status } : s)),
      selectedService:
        state.selectedService?.id === serviceId
          ? { ...state.selectedService, status }
          : state.selectedService,
    }));
  },

  updateServiceProgress: (serviceId, progress) => {
    set((state) => {
      const updatedServices = state.services.map(
        (s): ServiceWithProgress => (s.id === serviceId ? { ...s, progress } : s),
      );
      const updatedSelected: ServiceWithProgress | null =
        state.selectedService?.id === serviceId
          ? { ...state.selectedService, progress }
          : state.selectedService;
      return {
        services: updatedServices,
        selectedService: updatedSelected,
      };
    });
  },

  clearServiceProgress: (serviceId) => {
    set((state) => {
      const updatedServices = state.services.map(
        (s): ServiceWithProgress => (s.id === serviceId ? { ...s, progress: undefined } : s),
      );
      const updatedSelected: ServiceWithProgress | null =
        state.selectedService?.id === serviceId
          ? { ...state.selectedService, progress: undefined }
          : state.selectedService;
      return {
        services: updatedServices,
        selectedService: updatedSelected,
      };
    });
  },

  setFilters: (filters) => {
    set((state) => ({
      filters: { ...state.filters, ...filters },
    }));
  },

  clearFilters: () => {
    set({ filters: initialFilters });
  },

  setLoading: (isLoading) => {
    set({ isLoading });
  },

  setLoadingService: (isLoadingService) => {
    set({ isLoadingService });
  },

  setError: (error) => {
    set({ error, isLoading: false, isLoadingService: false });
  },

  clearError: () => {
    set({ error: null });
  },

  getFilteredServices: () => {
    const { services, filters } = get();
    let filtered = [...services];

    // Filter by status
    if (filters.status && filters.status !== 'ALL') {
      filtered = filtered.filter((s) => s.status === filters.status);
    }

    // Filter by search term
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(
        (s) =>
          s.name.toLowerCase().includes(searchLower) ||
          s.description?.toLowerCase().includes(searchLower) ||
          s.inputUrl.toLowerCase().includes(searchLower),
      );
    }

    return filtered;
  },

  getServiceById: (id) => {
    return get().services.find((s) => s.id === id);
  },

  getStats: () => {
    const { services } = get();
    return {
      total: services.length,
      running: services.filter((s) => s.status === 'RUNNING').length,
      stopped: services.filter((s) => s.status === 'STOPPED').length,
      error: services.filter((s) => s.status === 'ERROR').length,
      waitingSource: services.filter((s) => s.status === 'WAITING_SOURCE').length,
      waitingDestination: services.filter((s) => s.status === 'WAITING_DESTINATION').length,
    };
  },

  reset: () => {
    set(initialState);
  },
}));

// ═══════════════════════════════════════════════════════════════════════════
// SELECTORS
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Selector: Get all services
 */
export const selectServices = (state: ServicesState & ServicesActions) => state.services;

/**
 * Selector: Get selected service
 */
export const selectSelectedService = (state: ServicesState & ServicesActions) =>
  state.selectedService;

/**
 * Selector: Get loading state
 */
export const selectIsLoading = (state: ServicesState & ServicesActions) => state.isLoading;

/**
 * Selector: Get error
 */
export const selectError = (state: ServicesState & ServicesActions) => state.error;

/**
 * Selector: Get filters
 */
export const selectFilters = (state: ServicesState & ServicesActions) => state.filters;

/**
 * Selector: Get running services
 */
export const selectRunningServices = (state: ServicesState & ServicesActions) =>
  state.services.filter((s) => s.status === 'RUNNING');

export default useServicesStore;
